import { CameraIntent } from './types'

export interface ResetNorthOptions {
  duration?: number
}

function pickViewportCenter (viewer: any, Cesium: any) {
  const scene = viewer?.scene
  const camera = viewer?.camera
  const canvas = scene?.canvas
  if (!scene || !camera || !canvas?.clientWidth || !canvas?.clientHeight) return null

  const center = new Cesium.Cartesian2(canvas.clientWidth / 2, canvas.clientHeight / 2)
  let target: any = null

  // Prefer the visible surface (for example a 3D model), then terrain and ellipsoid.
  try {
    if (scene.pickPositionSupported && Cesium.defined(scene.pick(center))) {
      target = scene.pickPosition(center)
    }
  } catch (error) { /* depth picking is unavailable for some imagery providers */ }

  if (!Cesium.defined(target)) {
    try {
      const ray = camera.getPickRay(center)
      if (ray) target = scene.globe?.pick(ray, scene)
    } catch (error) { /* the current view may not intersect loaded terrain */ }
  }

  if (!Cesium.defined(target)) {
    try {
      target = camera.pickEllipsoid(center, scene.globe?.ellipsoid)
    } catch (error) { /* no globe intersection */ }
  }

  return Cesium.defined(target) ? target : null
}

/**
 * Rotate the current view to north while preserving the visible center, pitch and range.
 * Returning false means the current view has no valid globe/surface intersection, in
 * which case moving the camera would be more surprising than leaving it unchanged.
 */
export function resetCameraNorthPreservingCenter (
  viewer: any,
  Cesium: any,
  options: ResetNorthOptions = {}
) {
  if (!viewer || viewer.isDestroyed?.()) return false

  const camera = viewer.camera
  const target = pickViewportCenter(viewer, Cesium)
  if (!target) return false

  const range = Cesium.Cartesian3.distance(camera.positionWC, target)
  if (!Number.isFinite(range) || range <= 0) return false

  const pitch = camera.pitch
  const duration = Math.max(0, Number(options.duration ?? 0.8))
  camera.flyToBoundingSphere(new Cesium.BoundingSphere(target, 0), {
    duration,
    offset: new Cesium.HeadingPitchRange(0, pitch, range),
    complete: () => viewer.scene?.requestRender?.(),
    cancel: () => viewer.scene?.requestRender?.()
  })
  return true
}

export class CameraManager {
  private sequence = 0
  private activeIntent: CameraIntent | null = null

  createIntent (intent: Omit<CameraIntent, 'id'>): CameraIntent {
    return { ...intent, id: ++this.sequence }
  }

  begin (viewer: any, intent: CameraIntent) {
    if (this.activeIntent && this.activeIntent.priority > intent.priority) return false
    this.cancel(viewer)
    this.activeIntent = intent
    return true
  }

  isCurrent (intentId: number) {
    return this.activeIntent?.id === intentId
  }

  complete (intentId: number) {
    if (this.activeIntent?.id === intentId) this.activeIntent = null
  }

  cancel (viewer?: any) {
    try { viewer?.camera?.cancelFlight?.() } catch (error) { /* viewer may be changing scenes */ }
    this.activeIntent = null
  }
}
